import React, { useState, useEffect, useContext, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import alarm from "../../assets/audio/alarm.mp3";
import api from "../../services/AxiosConnection";
import AuthContext from "../../context/AuthContext";
import { Vortex } from "react-loader-spinner";
import { useNotification } from "../../context/NotificationContext";
import { RxCross2 } from "react-icons/rx";

const WarningsDialog = ({ warnings }) => {
  const [open, setOpen] = useState(false);
  const [currentWarningIndex, setCurrentWarningIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const { triggerNotification } = useNotification();
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const sourceRef = useRef(null);
  const { selectedSite } = useContext(AuthContext);
  const user = JSON.parse(localStorage.getItem("user"));


  // Initialize Web Audio API
  useEffect(() => {
    // Create audio context
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioContextRef.current = new AudioContext();


    // Fetch and decode audio file
    fetch(alarm)
      .then(response => response.arrayBuffer())
      .then(buffer => audioContextRef.current.decodeAudioData(buffer))
      .then(decodedBuffer => {
        audioBufferRef.current = decodedBuffer;
        setIsAudioLoaded(true);
      })
      .catch(error => {
        console.error("Error loading alarm sound:", error);
      });


    return () => {
      // Clean up audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);


  // Play alarm sound using Web Audio API
  const playAlarm = () => {
    if (!audioContextRef.current || !audioBufferRef.current) return;

    // Resume audio context if suspended
    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume();
    }

    // Stop any existing playback
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch (e) {}
    }

    // Create and play new source
    try {
      sourceRef.current = audioContextRef.current.createBufferSource();
      sourceRef.current.buffer = audioBufferRef.current;
      sourceRef.current.loop = true;
      sourceRef.current.connect(audioContextRef.current.destination);
      sourceRef.current.start();
    } catch (e) {
      console.error("Error playing warning sound:", e);
    }
  };

  // Stop alarm sound
  const stopAlarm = () => {
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch (e) {}
      sourceRef.current = null;
    }
  };

  useEffect(() => {
    if (warnings.length > 0) {
      // console.log("Warnings available", warnings);
      setOpen(true);
      setCurrentWarningIndex(0);
    } else {
      setOpen(false);
    }
  }, [warnings.length]);


  useEffect(() => {
    if (open && isAudioLoaded) {
      // Play alarm immediately when dialog opens and audio is ready
      playAlarm();

      // Clean up when dialog closes
      return () => {
        stopAlarm();
      };
    } else {
      stopAlarm();
    }
  }, [open, isAudioLoaded]);

  const handleAcknowledge = async () => {
    const currentWarning = warnings[currentWarningIndex];
    if (!currentWarning) return;

    setLoading(true);
    try {
      const response = await api.put(
        "/simple-view/updateTemperatureAlertAcknowledgedStatus",
        {
          siteDb: selectedSite,
          alertIds: [currentWarning.ID],
          acknowledgedBy: user.username,
        }
      );

      if (response.status === 200) {
        triggerNotification(`Warning Acknowledged Successfully!`, "success");
        if (currentWarningIndex < warnings.length - 1) {
          setCurrentWarningIndex(currentWarningIndex + 1);
        } else {
          setOpen(false);
        }
      }
    } catch (error) {
      triggerNotification(`Unable to Acknowledge the Warning!`, "error");
      console.error("Error acknowledging warning:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnore = () => {
    if (currentWarningIndex < warnings.length - 1) {
      setCurrentWarningIndex(currentWarningIndex + 1);
    } else {
      setOpen(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Get color class based on warning color and component type
  const getWarningColorClass = (side, componentType) => {
    // Only apply color if the current warning's side matches this component
    if (currentWarning.Side !== side) return '';

    const warningColor = currentWarning.Color ? currentWarning.Color.toLowerCase() : 'yellow';
    if (warningColor === 'orange') {
      return 'bg-orange-500';
    } else {
      return 'bg-yellow-500';
    }
  };

  // Get color style for header and button
  const getColorStyle = (warningColor) => {
    // Default to yellow if no color specified
    if (!warningColor) {
      return {
        main: "#eab308", // Regular yellow
        hover: "#ca8a04",
      };
    }

    const color = warningColor.toLowerCase();

    // Map of color names to their hex values
    const colorMap = {
      yellow: {
        main: "#fde047", // Light yellow
        hover: "#facc15", // Slightly darker yellow
      },
      orange: {
        main: "#f97316",
        hover: "#ea580c",
      },
      // Default fallback
      default: {
        main: "#eab308",
        hover: "#ca8a04",
      }
    };

    return colorMap[color] || colorMap.default;
  };

  // Function to determine side display
  const getSideDisplay = (side) => {
    if (["Ts1", "Ts3", "Ts5"].includes(side)) return "Left";
    if (["Ts2", "Ts4", "Ts6"].includes(side)) return "Right";
    return side || "N/A";
  };

  const currentWarning = warnings[currentWarningIndex];
  if (!currentWarning) return null;

  const warningColor = currentWarning.Color;
  const colorStyle = getColorStyle(warningColor);

  return (
    <div>
      <style>{`
        .warning-wheel:not(.bg-yellow-500):not(.bg-orange-500) {
          background: linear-gradient(90deg, #a69885 0%, #c2b6a3 50%, #a69885 100%);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
        }
        .warning-wheel-inner:not(.bg-yellow-500):not(.bg-orange-500) {
          background: linear-gradient(90deg, #a69885 0%, #c2b6a3 50%, #a69885 100%);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
        }
        .warning-brake-disc:not(.bg-yellow-500):not(.bg-orange-500) {
          background: linear-gradient(90deg, #b0b0b0 0%, #d1d1d1 50%, #b0b0b0 100%);
        }
        .warning-brake-slot {
          background: #6b6b6b;
        }
        .warning-axle {
          background: linear-gradient(180deg, #f0f0f0 0%, #d5d5d5 50%, #f0f0f0 100%);
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
        }
        .warning-axle-connector:not(.bg-yellow-500):not(.bg-orange-500) {
          background: linear-gradient(180deg, #d5d5d5 0%, #c0c0c0 50%, #d5d5d5 100%);
        }
        .warning-axle-highlight:not(.bg-yellow-500):not(.bg-orange-500) {
          background: transparent;
        }
        .warning-hub {
          background: linear-gradient(90deg, #808080 0%, #606060 100%);
        }
      `}</style>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="warnings-dialog-title"
        aria-describedby="warnings-dialog-description"
        scroll="paper"
        fullWidth
        maxWidth="md"
        sx={{ borderRadius: "8px" }}
      >
        <div
          className="flex justify-between items-center px-6 py-4"
          style={{ backgroundColor: colorStyle.main }}
        >
          <DialogTitle
            id="warnings-dialog-title"
            className="font-extrabold text-white"
            sx={{ padding: 0 }}
          >
            NOVIUS HABD Warning {currentWarningIndex + 1} of {warnings.length}
          </DialogTitle>
          <button
            onClick={handleClose}
            className="text-white hover:text-red-400 text-xl"
            aria-label="Close Warning Dialog"
          >
            <RxCross2 />
          </button>
        </div>

        <DialogContent dividers className="bg-gray-100">
          <DialogContentText id="warnings-dialog-description" component="div">
            <div className="flex flex-row space-x-12">
              {/* Axle and Wheel Visualization */}
              <div className="w-1/2">
                <div className="relative w-full max-w-md h-48 flex items-center justify-center">
                  {/* Left Wheel Assembly */}
                  <div className="absolute left-4 flex items-center z-20">
                    <div
                      className={`warning-wheel h-40 w-6 flex items-center justify-start relative rounded-l-md ${getWarningColorClass("Ts3", "wheel")
                        }`}
                    >
                      <div
                        className={`warning-wheel-inner h-48 w-3 ml-3 absolute left-0 rounded-full ${getWarningColorClass("Ts3", "wheel-inner")
                          }`}
                      ></div>
                      <div
                        className={`warning-brake-disc h-32 w-3 rounded-l-sm absolute left-12 flex items-center justify-center z-20 ${getWarningColorClass("Ts5", "brake-disc")
                          }`}
                      >
                        <div className="flex flex-col space-y-2 absolute">
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                        </div>
                      </div>
                      <div className="warning-hub h-12 w-1.5 rounded-l-sm absolute left-6 z-0"></div>
                    </div>
                  </div>
                  <div
                    className={`absolute left-12 top-1/2 -translate-y-1/2 warning-axle-connector h-9 w-6 z-10`}
                  ></div>
                  <div className="relative warning-axle h-8 w-full max-w-md z-0">
                    <div
                      className={`absolute left-0 warning-axle-highlight h-8 w-6 z-10 ${getWarningColorClass("Ts1", "axle")
                        }`}
                    ></div>
                    <div
                      className={`absolute right-0 warning-axle-highlight h-8 w-6 z-10 ${getWarningColorClass("Ts2", "axle")
                        }`}
                    ></div>
                  </div>
                  <div
                    className={`absolute right-12 top-1/2 -translate-y-1/2 warning-axle-connector h-9 w-6 z-10`}
                  ></div>
                  <div className="absolute right-4 flex items-center z-20">
                    <div
                      className={`warning-wheel h-40 w-6 flex items-center justify-end relative rounded-r-md ${getWarningColorClass("Ts4", "wheel")
                        }`}
                    >
                      <div
                        className={`warning-wheel-inner h-48 w-3 rounded-full mr-3 absolute right-0 ${getWarningColorClass("Ts4", "wheel-inner")
                          }`}
                      ></div>
                      <div
                        className={`warning-brake-disc h-32 w-3 rounded-r-sm absolute right-12 flex items-center justify-center ${getWarningColorClass("Ts6", "brake-disc")
                          }`}
                      >
                        <div className="flex flex-col space-y-2 absolute">
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                          <div className="warning-brake-slot h-3 w-1.5"></div>
                        </div>
                      </div>
                      <div className="warning-hub h-12 w-1.5 rounded-r-sm absolute right-6"></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Warning Details */}
              <div className="w-1/2 space-y-4">
                <h3 className="font-bold text-lg">
                  Axle No: {currentWarning.AxelNo}
                </h3>
                <div className="space-y-2">
                  {warningColor && (
                    <p className="text-sm">
                      <strong>Warning Type:</strong> Temperature Difference Warning
                    </p>
                  )}
                  <p className="text-sm">
                    <strong>Coach Position:</strong>{" "}
                    {currentWarning.CoachPosition || "N/A"}
                  </p>
                  <p className="text-sm">
                    <strong>Coach No:</strong> {currentWarning.CoachNo || "N/A"}
                  </p>
                  <p className="text-sm">
                    <strong>Side:</strong> {getSideDisplay(currentWarning.Side)}
                  </p>
                  <p className="text-sm">
                    <strong>Temperature:</strong> {currentWarning.Temperature}{" "}
                    °C
                  </p>
                  <p className="text-sm">
                    <strong>Acknowledged By:</strong>{" "}
                    {currentWarning.AcknowledgedBy || "Not Acknowledged Yet."}
                  </p>
                </div>
              </div>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions className="flex justify-center py-4 space-x-4">
          <Button
            onClick={handleAcknowledge}
            disabled={loading}
            sx={{
              backgroundColor: colorStyle.main,
              color: "white",
              padding: "10px 20px",
              borderRadius: "5px",
              "&:hover": {
                backgroundColor: colorStyle.hover,
              },
            }}
          >
            {loading ? (
              <Vortex
                visible={true}
                height="20"
                width="20"
                ariaLabel="vortex-loading"
                colors={["white", "white", "white", "white", "white", "white"]}
              />
            ) : (
              "Acknowledge"
            )}
          </Button>
          <Button
            onClick={handleIgnore}
            sx={{
              backgroundColor: "#6b7280",
              color: "white",
              padding: "10px 20px",
              borderRadius: "5px",
              "&:hover": {
                backgroundColor: "#4b5563",
              },
            }}
          >
            Ignore
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WarningsDialog;